import React from 'react'
import { LEAGUE_STATUS } from '../../../constants/leagueStatuses'
import BracketDiagram from '../../bracket/BracketDiagram'
import EditableBracketDiagram from '../../bracket/EditableBracketDiagram'

/**
 * BracketManagementTab - Displays playoff bracket with view/edit modes
 *
 * @param {Object} props
 * @param {Object|null} props.bracket - Bracket data with rounds array
 * @param {Object} props.league - League object with status
 * @param {number} props.leagueId - League ID for bracket operations
 * @param {Array} props.activeLevels - Active skill levels for selected league
 * @param {boolean} props.hasLevelTabs - Whether to render level tabs
 * @param {number|null} props.selectedBracketLevelId - Selected level id
 * @param {string|null} props.selectedBracketLevelSkill - Selected level display label
 * @param {boolean} props.isEditMode - Whether bracket is in edit mode
 * @param {boolean} props.isPublishing - Whether bracket is being published
 * @param {boolean} props.isLoading - Whether bracket is loading
 * @param {Function} props.onToggleEditMode - Handler to toggle edit mode
 * @param {Function} props.onPublishBracket - Handler to publish bracket
 * @param {Function} props.onBracketUpdate - Handler when bracket is updated
 * @param {Function} props.onRefresh - Handler to refresh bracket data
 * @returns {JSX.Element}
 */
function BracketManagementTab({
  bracket,
  league,
  leagueId,
  activeLevels = [],
  hasLevelTabs = false,
  selectedBracketLevelId = null,
  selectedBracketLevelSkill = null,
  isEditMode,
  isPublishing,
  isLoading,
  onToggleEditMode,
  onBracketLevelChange = () => {},
  onPublishBracket,
  onBracketUpdate,
  onRefresh,
}) {
  const isSetupMode = league?.league_status === LEAGUE_STATUS.PLAYOFFS_SETUP

  const handleSave = async (updatedBracket) => {
    await onBracketUpdate(updatedBracket)
    onToggleEditMode(false)
  }

  if (isLoading) {
    return (
      <div
        className="text-center py-5"
        role="status"
        aria-label="Loading bracket"
      >
        <div className="spinner-border text-primary">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-3 text-muted">Loading bracket...</p>
      </div>
    )
  }

  return (
    <div role="region" aria-label="Bracket management">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h4>
          <i className="fas fa-trophy me-2"></i>
          Playoff Bracket
          {selectedBracketLevelSkill && (
            <span className="text-muted fs-6 ms-2">
              — Level {selectedBracketLevelSkill}
            </span>
          )}
        </h4>
        <div className="d-flex gap-2 align-items-center">
          <span className="badge bg-purple">
            {bracket?.rounds?.length || 0} rounds
          </span>
          {isSetupMode && bracket && !isEditMode && (
            <>
              <button
                className="btn btn-secondary btn-sm"
                onClick={() => onToggleEditMode(true)}
                disabled={isLoading}
                aria-label="Edit bracket seeds"
              >
                <i className="fas fa-edit me-1"></i>
                Edit Seeds
              </button>
              <button
                className="btn btn-primary btn-sm"
                onClick={() => onPublishBracket(league)}
                disabled={isPublishing}
                aria-label="Publish bracket to users"
              >
                {isPublishing ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-1" />
                    Publishing...
                  </>
                ) : (
                  <>
                    <i className="fas fa-check me-1"></i>
                    Publish Bracket
                  </>
                )}
              </button>
            </>
          )}
        </div>
      </div>

      {hasLevelTabs && (
        <div className="row mb-3">
          <div className="col-12">
            <ul className="nav nav-tabs admin-bracket-level-tabs">
              {activeLevels.map((level) => (
                <li className="nav-item" key={level.id}>
                  <button
                    type="button"
                    className={`nav-link ${
                      selectedBracketLevelId === level.id ? 'active' : ''
                    }`}
                    data-testid={`bracket-level-tab-${level.id}`}
                    onClick={() => onBracketLevelChange(level.id)}
                  >
                    {level.skill_level}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {isSetupMode && !isEditMode && (
        <div className="alert alert-info mb-4" role="alert">
          <h5 className="alert-heading">
            <i className="fas fa-eye-slash me-2"></i>
            Bracket Setup Mode
          </h5>
          <p className="mb-0">
            The bracket has been generated and is ready for review. Users cannot
            see the playoffs yet. Click <strong>Edit Seeds</strong> to adjust
            seeding, then <strong>Publish Bracket</strong> when ready.
          </p>
        </div>
      )}

      {!bracket ? (
        <div className="text-center py-5">
          <i className="fas fa-trophy fa-3x text-muted mb-3"></i>
          <h5 className="text-muted">No bracket data found</h5>
          <p className="text-muted">
            The playoff bracket will appear here once playoffs have been
            started.
          </p>
          <button
            className="btn btn-outline-primary"
            onClick={onRefresh}
            aria-label="Refresh bracket data"
          >
            <i className="fas fa-refresh me-2"></i>
            Refresh Bracket
          </button>
        </div>
      ) : isEditMode ? (
        <EditableBracketDiagram
          bracket={bracket}
          leagueId={leagueId}
          onBracketUpdate={handleSave}
          onCancel={() => onToggleEditMode(false)}
          height={600}
        />
      ) : bracket.rounds && bracket.rounds.length > 0 ? (
        <BracketDiagram bracket={bracket} height={600} />
      ) : (
        <div className="text-center py-5 text-muted" role="status">
          No rounds found
        </div>
      )}
    </div>
  )
}

export default BracketManagementTab
